import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  QuarterlyStatistics,
  SafetyKPI,
  CreateQuarterlyStatisticsPayload,
  GenerateQuarterlyStatsPayload,
  CalculateKPIPayload,
} from '@/lib/types';

// ─── Quarterly Statistics ───────────────────────────────────────────────────

export interface QuarterlyStatsFilters {
  [key: string]: unknown;
  year?: number;
  quarter?: number;
  status?: string;
}

function cleanFilters(filters: Record<string, unknown>): Record<string, string> {
  const cleaned: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = String(value);
    }
  }
  return cleaned;
}

export function useQuarterlyStatsList(filters: QuarterlyStatsFilters = {}) {
  return useQuery({
    queryKey: queryKeys.compliance.quarterlyStatsList(filters),
    queryFn: async (): Promise<QuarterlyStatistics[]> => {
      const params = cleanFilters(filters as Record<string, unknown>);
      const response = await apiClient.get<{ results: QuarterlyStatistics[] } | QuarterlyStatistics[]>(
        '/api/v1/compliance/quarterly-statistics/',
        { params },
      );
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray((data as { results: QuarterlyStatistics[] }).results)) return (data as { results: QuarterlyStatistics[] }).results;
      return [];
    },
  });
}

export function useQuarterlyStatsDetail(id: number) {
  return useQuery({
    queryKey: queryKeys.compliance.quarterlyStatsDetail(id),
    queryFn: async (): Promise<QuarterlyStatistics> => {
      const response = await apiClient.get<QuarterlyStatistics>(
        `/api/v1/compliance/quarterly-statistics/${id}/`,
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateQuarterlyStats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateQuarterlyStatisticsPayload): Promise<QuarterlyStatistics> => {
      const response = await apiClient.post<QuarterlyStatistics>(
        '/api/v1/compliance/quarterly-statistics/',
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.quarterlyStats() });
    },
  });
}

export function useUpdateQuarterlyStats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateQuarterlyStatisticsPayload>;
    }): Promise<QuarterlyStatistics> => {
      const response = await apiClient.patch<QuarterlyStatistics>(
        `/api/v1/compliance/quarterly-statistics/${id}/`,
        data,
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.quarterlyStats() });
      qc.invalidateQueries({
        queryKey: queryKeys.compliance.quarterlyStatsDetail(variables.id),
      });
    },
  });
}

export function useGenerateQuarterlyStats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: GenerateQuarterlyStatsPayload): Promise<QuarterlyStatistics> => {
      const response = await apiClient.post<QuarterlyStatistics>(
        '/api/v1/compliance/quarterly-statistics/generate/',
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.quarterlyStats() });
    },
  });
}

export function useSubmitQuarterlyStats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<QuarterlyStatistics> => {
      const response = await apiClient.post<QuarterlyStatistics>(
        `/api/v1/compliance/quarterly-statistics/${id}/submit/`,
      );
      return response.data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.quarterlyStats() });
      qc.invalidateQueries({
        queryKey: queryKeys.compliance.quarterlyStatsDetail(id),
      });
    },
  });
}

// ─── Safety KPIs ────────────────────────────────────────────────────────────

export interface SafetyKPIFilters {
  [key: string]: unknown;
  period_type?: string;
}

export function useSafetyKPITrends(filters: SafetyKPIFilters = {}) {
  return useQuery({
    queryKey: queryKeys.compliance.safetyKpiTrends(filters),
    queryFn: async (): Promise<SafetyKPI[]> => {
      const params = cleanFilters(filters as Record<string, unknown>);
      const response = await apiClient.get<SafetyKPI[]>(
        '/api/v1/compliance/safety-kpis/trends/',
        { params },
      );
      return response.data;
    },
  });
}

export function useCalculateSafetyKPIs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CalculateKPIPayload): Promise<SafetyKPI> => {
      const response = await apiClient.post<SafetyKPI>(
        '/api/v1/compliance/safety-kpis/calculate/',
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.safetyKpis() });
    },
  });
}
